import { projectSnapshotDto } from '@/services/projectSnapshot/ProjectSnapshot';
import { decodeProjectSnapshot } from '@/services/projectSnapshot/CurrentProjectFormat';
import { decompressProjectContainer } from '@/services/projectSnapshot/ProjectCompression';

export const MAX_SHARE_BYTES = 15_000_000;
export const SHARE_EXPANDED_BYTES = 64 * 1024 * 1024;
const MANIFEST_BYTES = 8 * 1024 * 1024;

export function assertShareSize(bytes: number): void {
  if (!Number.isSafeInteger(bytes) || bytes < 16 || bytes > MAX_SHARE_BYTES)
    throw new Error('Shared projects must be at most 15 MB (15,000,000 bytes).');
}

/** Bound JSON work before parsing or schema traversal, including hostile nesting. */
function checkJsonBudget(text: string): void {
  let depth = 0,
    quoted = false,
    escaped = false,
    members = 0;
  for (const char of text) {
    if (quoted) {
      if (escaped) escaped = false;
      else if (char === '\\') escaped = true;
      else if (char === '"') quoted = false;
    } else if (char === '"') quoted = true;
    else if (char === '{' || char === '[') {
      if (++depth > 64) throw new Error('Shared project nesting exceeds safety limit.');
    } else if (char === '}' || char === ']') depth--;
    else if (char === ',' && ++members > 500_000)
      throw new Error('Shared project metadata exceeds safety limit.');
  }
}

/** Sharing admits the current compact release only. Local import/migration is unchanged. */
export async function readSharedProjectFile(blob: Blob, signal?: AbortSignal) {
  assertShareSize(blob.size);
  signal?.throwIfAborted();
  const raw = await decompressProjectContainer(
    blob,
    SHARE_EXPANDED_BYTES,
    undefined,
    signal,
  );
  if (raw.size < 16 || raw.size > SHARE_EXPANDED_BYTES)
    throw new Error('Shared project expansion exceeds safety limit.');
  const header = new DataView(await raw.slice(0, 16).arrayBuffer());
  if ((await raw.slice(0, 8).text()) !== 'DGTLCMP6')
    throw new Error('Share a project saved by the current DigiTiles version.');
  const length = header.getUint32(8, true);
  if (length > MANIFEST_BYTES || length + 16 > raw.size)
    throw new Error('Shared project manifest exceeds safety limit.');
  const text = await raw.slice(16, 16 + length).text();
  checkJsonBudget(text);
  const manifest = JSON.parse(text);
  if (
    !Array.isArray(manifest.records) ||
    manifest.records.length > 10_000 ||
    !Array.isArray(manifest.project?.chips) ||
    manifest.project.chips.length > 512
  )
    throw new Error('Shared project structure exceeds safety limit.');
  let references = 0;
  for (const record of manifest.records) {
    if (
      !Array.isArray(record?.dependencies) ||
      (references += record.dependencies.length) > 100_000
    )
      throw new Error('Shared project references exceed safety limit.');
  }
  signal?.throwIfAborted();
  const snapshot = await decodeProjectSnapshot(raw, undefined, signal, {
    currentVersionOnly: true,
    tiles: 100_000,
    tileBytes: 32 * 1024 * 1024,
    runtimeEdges: 200_000,
    runtimeBindings: 200_000,
    runtimeIdentityUnits: 2 * 1024 * 1024,
    scalarParts: 100_000,
    scalarText: 2 * 1024 * 1024,
  });
  const chips = new Map(snapshot.chips.map((chip) => [chip.id, chip]));
  const visiting = new Set<string>(),
    visited = new Map<string, { expanded: number; height: number }>();
  const visit = (id: string, depth = 0): { expanded: number; height: number } => {
    if (depth > 64 || visiting.has(id))
      throw new Error('Shared project has cyclic chip references.');
    if (visited.has(id)) return visited.get(id)!;
    const chip = chips.get(id);
    if (!chip || chip.editingInstanceDto.chipId !== id)
      throw new Error('Shared project has invalid chip references.');
    visiting.add(id);
    let expanded =
      1 +
      chip.logicCircuitDto.tileManagerDto.tiles.length +
      chip.structureCircuitDto.tileManagerDto.tiles.length;
    let height = 1;
    for (const instance of chip.chipInstanceDtos) {
      const child = visit(instance.chipId, depth + 1);
      expanded += child.expanded;
      height = Math.max(height, child.height + 1);
      if (height > 64)
        throw new Error('Shared project chip nesting exceeds safety limit.');
      if (expanded > 100_000)
        throw new Error('Shared project nested chip expansion exceeds safety limit.');
    }
    visiting.delete(id);
    const budget = { expanded, height };
    visited.set(id, budget);
    return budget;
  };
  for (const id of chips.keys()) visit(id);
  let nodes = 0,
    identityUnits = 0,
    graphs = 0;
  for (const chip of snapshot.chips) {
    for (const instance of [...chip.chipInstanceDtos, chip.editingInstanceDto]) {
      nodes += instance.logicGraphDto.nodes.count;
      identityUnits += instance.logicGraphDto.nodes.identityCodeUnits;
      if (++graphs > 2048 || nodes > 100_000 || identityUnits > 2 * 1024 * 1024)
        throw new Error('Shared project graph expansion exceeds safety limit.');
    }
  }
  signal?.throwIfAborted();
  return projectSnapshotDto(snapshot);
}
